/**
 * Human-readable labels for SPA analytics (avoid logging raw dynamic segments as titles).
 */

export interface RouteAnalyticsMatch {
   pageTitle: string;
   /** Pattern with placeholders e.g. /albums/public/:albumId */
   routeTemplate: string;
   featureArea: string;
}

/** More specific paths must appear before their prefixes. */
const STATIC_ROUTES: { path: string; pageTitle: string; featureArea: string }[] = [
   { path: '/', pageTitle: 'Landing', featureArea: 'marketing' },
   { path: '/get-started', pageTitle: 'Get started first album', featureArea: 'onboarding' },
   { path: '/signup/venue', pageTitle: 'Venue signup', featureArea: 'auth' },
   { path: '/signup', pageTitle: 'Signup', featureArea: 'auth' },
   { path: '/phone-signup', pageTitle: 'Phone signup', featureArea: 'auth' },
   { path: '/login', pageTitle: 'Login', featureArea: 'auth' },
   { path: '/accept-invitation', pageTitle: 'Accept invitation', featureArea: 'auth' },
   { path: '/accept-album-invitation', pageTitle: 'Accept album invitation', featureArea: 'auth' },
   { path: '/accept-network-invite', pageTitle: 'Accept network invite', featureArea: 'auth' },
   { path: '/accept-network-invitation', pageTitle: 'Accept network invitation', featureArea: 'auth' },
   { path: '/privacy', pageTitle: 'Privacy', featureArea: 'marketing' },
   { path: '/terms', pageTitle: 'Terms', featureArea: 'marketing' },
   { path: '/contact', pageTitle: 'Contact', featureArea: 'marketing' },
   { path: '/help', pageTitle: 'Help', featureArea: 'marketing' },
   { path: '/welcome', pageTitle: 'Welcome', featureArea: 'onboarding' },
   { path: '/feed', pageTitle: 'Feed', featureArea: 'feed' },
   { path: '/dashboard', pageTitle: 'Dashboard', featureArea: 'core' },
   { path: '/map', pageTitle: 'Map', featureArea: 'photos' },
   { path: '/upload', pageTitle: 'Upload photos', featureArea: 'photos' },
   { path: '/timeline', pageTitle: 'Timeline', featureArea: 'photos' },
   { path: '/manage', pageTitle: 'Photo management', featureArea: 'photos' },
   { path: '/trips', pageTitle: 'Trips', featureArea: 'trips' },
   { path: '/trip-planner', pageTitle: 'Trip planner', featureArea: 'trips' },
   { path: '/itinerary', pageTitle: 'Itinerary builder', featureArea: 'trips' },
   { path: '/calendar', pageTitle: 'Calendar', featureArea: 'events' },
   { path: '/people', pageTitle: 'People', featureArea: 'social' },
   { path: '/inbox', pageTitle: 'Inbox', featureArea: 'social' },
   { path: '/chat', pageTitle: 'Chat', featureArea: 'social' },
   { path: '/organizations', pageTitle: 'Organizations', featureArea: 'orgs' },
   { path: '/albums', pageTitle: 'Albums', featureArea: 'albums' },
   { path: '/ambassador', pageTitle: 'Ambassador', featureArea: 'marketing' },
   { path: '/about', pageTitle: 'About', featureArea: 'marketing' },
   { path: '/features', pageTitle: 'Features', featureArea: 'marketing' },
   { path: '/public-subscription', pageTitle: 'Public subscription', featureArea: 'billing' },
   { path: '/reset-password-email', pageTitle: 'Reset password email', featureArea: 'auth' },
   { path: '/reset-password', pageTitle: 'Reset password', featureArea: 'auth' },
   { path: '/canva-callback', pageTitle: 'Canva callback', featureArea: 'integrations' },
];

function matchPrefix(pathname: string, prefix: string): boolean {
   return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Resolve analytics labels for the current pathname (no query string).
 */
export function resolveRouteAnalytics(pathname: string): RouteAnalyticsMatch {
   const path = pathname.split('?')[0] || '/';

   for (const row of STATIC_ROUTES) {
      if (path === row.path || (row.path !== '/' && matchPrefix(path, row.path))) {
         return {
            pageTitle: row.pageTitle,
            routeTemplate: row.path,
            featureArea: row.featureArea,
         };
      }
   }

   if (path.startsWith('/albums/public/')) {
      return {
         pageTitle: 'Public album',
         routeTemplate: '/albums/public/:albumId',
         featureArea: 'albums_public',
      };
   }
   if (path.startsWith('/events/public/')) {
      return {
         pageTitle: 'Public event',
         routeTemplate: '/events/public/:eventId',
         featureArea: 'events_public',
      };
   }
   if (path.startsWith('/events/embed/')) {
      return {
         pageTitle: 'Event embed',
         routeTemplate: '/events/embed/:publicLinkId',
         featureArea: 'events_public',
      };
   }
   if (path.startsWith('/shared/album/')) {
      return {
         pageTitle: 'Shared album viewer',
         routeTemplate: '/shared/album/:shareToken',
         featureArea: 'albums_public',
      };
   }
   if (path.startsWith('/shared/collection/')) {
      return {
         pageTitle: 'Shared collection',
         routeTemplate: '/shared/collection/:shareToken',
         featureArea: 'sharing',
      };
   }
   if (path.startsWith('/social/collection/')) {
      return {
         pageTitle: 'Social shared collection',
         routeTemplate: '/social/collection/:shareToken',
         featureArea: 'sharing',
      };
   }
   if (path.startsWith('/subscribe/')) {
      return {
         pageTitle: 'Org subscribe',
         routeTemplate: '/subscribe/:orgId',
         featureArea: 'billing',
      };
   }
   if (path.startsWith('/profile/public/')) {
      return {
         pageTitle: 'Public profile QR',
         routeTemplate: '/profile/public/:userId',
         featureArea: 'marketing',
      };
   }
   if (path.startsWith('/qr/')) {
      return {
         pageTitle: 'QR share',
         routeTemplate: '/qr/:userId',
         featureArea: 'marketing',
      };
   }
   if (path.startsWith('/trip/')) {
      return {
         pageTitle: 'Trip detail',
         routeTemplate: '/trip/:tripId',
         featureArea: 'trips',
      };
   }
   if (path.startsWith('/photo/')) {
      return {
         pageTitle: 'Photo detail',
         routeTemplate: '/photo/:id',
         featureArea: 'photos',
      };
   }
   if (path.startsWith('/organizations/')) {
      return {
         pageTitle: 'Organization',
         routeTemplate: '/organizations/*',
         featureArea: 'orgs',
      };
   }
   if (path.startsWith('/venues/')) {
      return {
         pageTitle: 'Venues',
         routeTemplate: '/venues/*',
         featureArea: 'venues',
      };
   }
   if (path.startsWith('/venue/')) {
      return {
         pageTitle: 'Venue',
         routeTemplate: '/venue/*',
         featureArea: 'venues',
      };
   }
   if (path.startsWith('/explore') || path.startsWith('/events/explore')) {
      return {
         pageTitle: 'Explore',
         routeTemplate: '/explore*',
         featureArea: 'events',
      };
   }

   // Public landing /username pattern: /:userId/:username — two segments, no reserved first segment
   const segments = path.split('/').filter(Boolean);
   if (segments.length === 2 && !segments[0].includes('.')) {
      return {
         pageTitle: 'Public landing',
         routeTemplate: '/:userId/:username',
         featureArea: 'marketing',
      };
   }

   return {
      pageTitle: 'App',
      routeTemplate: path || '/',
      featureArea: 'other',
   };
}
